import React, { useState, useEffect } from "react";
import Joyride, {
  ACTIONS,
  CallBackProps,
  EVENTS,
  STATUS,
  Step,
} from "react-joyride";
import { useTour } from "@/contexts/TourContext";

// Define the steps for the tour
const defaultSteps: Step[] = [
  {
    target: "body",
    content:
      "Welcome to Hirelo! Let's take a quick tour of our platform to help you get started.",
    placement: "center",
    disableBeacon: true,
  },
  {
    target: ".dashboard-tour",
    content:
      "This is your main dashboard where you can see an overview of your payroll status. You can see the total employment cost for the month, if you have any hours, PTO, or expenses to approve and the status of all your payruns.",
    placement: "bottom",
  },
  {
    target: ".parent-information",
    content:
      "This is where you enter information about yourself as a parent. We need this information to properly register you with the HMRC and the Pensions Regulator.",
    placement: "right",
  },
  {
    target: ".employer-management",
    content:
      "Configure employer settings, like PAYE reference and HMRC credentials. Unless you are transferring from another payroll provider, you shouldn't have to do anything here. Even if you are transferring from a payroll provider, Hirelo will guide you through all the steps to acquire the necessary information.",
    placement: "right",
  },
  {
    target: "#employee-section",
    content:
      "Here you can manage your nanny's profile and employment details. You can update personal details like their address, name, phone number, salary, etc. You can also update their HMRC information such as their tax information, P45 details, etc.",
    placement: "right",
  },
  {
    target: ".pension-management",
    content:
      "Set up and manage pension contributions for your nanny. Even if your nanny is opting out of Pension contributions, this is something we need to know in order to properly relay the information to the Pensions Regulator.",
    placement: "right",
  },
  {
    target: "#tax-section",
    content:
      "View and manage all the relevant HMRC filings documents (FPS, EPS, P45, P60, P11, etc). Hirelo handles the generation and storage, so this is mainly for viewing.",
    placement: "right",
  },
  {
    target: ".earnings-management",
    content:
      "Define custom earning rates here! For example, if your nanny works overtime or proxy parents, you can define custom rates for this work making it easier to track.",
    placement: "right",
  },
  {
    target: ".pto-management",
    content:
      "Configure and manage Paid Time Off policies and balances. This is where you can see how much time off your nanny has taken, make changes to policies if needed, etc.",
    placement: "right",
  },
  {
    target: ".submission-management",
    content:
      "Approve timesheets, expenses, or PTO requests submitted by your nanny. Every pay period your nanny will enter in any additional hours, expenses, or time off and you can approve it in a click of a button!",
    placement: "right",
  },
  {
    target: ".contract-management",
    content:
      "Manage employment contracts and related documents here. This should be a one time event that happens when you first hire your nanny.",
    placement: "right",
  },
  {
    target: "#payrun-overview",
    content:
      "This section allows you to process payroll and view payment history. Make changes to any overtime hours, expense reimbursements, or any other manual changes you need to make!",
    placement: "bottom",
    locale: { last: "Finish" },
  },
];

export const GuidedTour: React.FC = () => {
  const { tourStatus, endTour } = useTour();
  const [steps] = useState<Step[]>(defaultSteps);
  const [run, setRun] = useState(false);

  // Start the tour when status is in_progress
  useEffect(() => {
    if (tourStatus === "in_progress") {
      setRun(true);
    } else {
      setRun(false);
    }
  }, [tourStatus]);

  const handleJoyrideCallback = (data: CallBackProps) => {
    const { action, index, status, type } = data;

    if (type === EVENTS.STEP_AFTER || type === EVENTS.TARGET_NOT_FOUND) {
      // Update state to advance the tour
      const nextStepIndex = index + (action === ACTIONS.PREV ? -1 : 1);

      // If we're on the last step and going forward, end the tour
      if (nextStepIndex >= steps.length && action === ACTIONS.NEXT) {
        endTour();
      }
    }

    // If the tour is finished or skipped, end it
    if (status === STATUS.FINISHED || status === STATUS.SKIPPED) {
      endTour();
    }
  };

  return (
    <>
      <Joyride
        callback={handleJoyrideCallback}
        continuous
        hideCloseButton
        run={run}
        scrollToFirstStep
        showProgress
        showSkipButton
        steps={steps}
        styles={{
          options: {
            arrowColor: "#fff",
            backgroundColor: "#fff",
            primaryColor: "#6366f1", // indigo-500
            textColor: "#334155", // slate-700
            overlayColor: "rgba(0, 0, 0, 0.5)",
            zIndex: 10000,
          },
          buttonNext: {
            backgroundColor: "#6366f1",
          },
          buttonBack: {
            color: "#6366f1",
          },
        }}
      />
    </>
  );
};
